/** Rolling window for "active" leaderboard players (≈2 months). */
export const ACTIVE_WINDOW_DAYS = 60;

export const LEADERBOARD_SCOPES = {
  ACTIVE: 'active',
  ALL: 'all',
};

/**
 * @param {string|null|undefined} scope
 * @returns {'active'|'all'}
 */
export const parseLeaderboardScope = (scope) =>
  scope === LEADERBOARD_SCOPES.ALL ? LEADERBOARD_SCOPES.ALL : LEADERBOARD_SCOPES.ACTIVE;

/**
 * Returns true when the player's last session falls within the active window
 * ending at referenceDate.
 *
 * @param {string|Date|null|undefined} lastSessionDate
 * @param {Date} [referenceDate=new Date()]
 * @param {number} [windowDays=ACTIVE_WINDOW_DAYS]
 */
export const isPlayerActiveAsOf = (
  lastSessionDate,
  referenceDate = new Date(),
  windowDays = ACTIVE_WINDOW_DAYS
) => {
  if (!lastSessionDate) return false;
  const last = new Date(lastSessionDate);
  if (Number.isNaN(last.getTime())) return false;
  const ref = new Date(referenceDate);
  const cutoff = new Date(ref);
  cutoff.setDate(cutoff.getDate() - windowDays);
  return last >= cutoff && last <= ref;
};

/**
 * @param {Array<{ players?: { id?: string }, sessionsPlayed?: number, lastSessionDate?: string|null }>} entries
 * @param {'active'|'all'} scope
 * @param {Record<string, { sessionsPlayed?: number, lastSessionDate?: string|null }>} [sessionSummaryByPlayerId]
 * @param {Date} [referenceDate]
 */
export const filterLeaderboardByScope = (
  entries,
  scope,
  sessionSummaryByPlayerId = {},
  referenceDate = new Date()
) => {
  const eligible = (entries ?? []).filter((item) => {
    const playerId = item.players?.id;
    const summary = sessionSummaryByPlayerId[playerId] ?? item;
    const sessionsPlayed = summary.sessionsPlayed ?? item.sessionsPlayed ?? 0;
    if (sessionsPlayed <= 0) return false;
    if (scope === LEADERBOARD_SCOPES.ALL) return true;
    const lastSessionDate = summary.lastSessionDate ?? item.lastSessionDate ?? null;
    return isPlayerActiveAsOf(lastSessionDate, referenceDate);
  });
  return eligible;
};

/**
 * Assigns 1-based ranks after sort order is applied. Mutates nothing — returns new array.
 *
 * @param {Array<Object>} sortedEntries
 * @param {(item: Object) => number} getElo
 * @param {(item: Object) => string|undefined} getPlayerId
 */
export const assignLeaderboardRanks = (sortedEntries, getElo, getPlayerId) => {
  const sorted = [...(sortedEntries ?? [])].sort((a, b) => {
    const eloDiff = (getElo(b) ?? 0) - (getElo(a) ?? 0);
    if (eloDiff !== 0) return eloDiff;
    const idA = getPlayerId(a) ?? '';
    const idB = getPlayerId(b) ?? '';
    return idA.localeCompare(idB);
  });

  return sorted.map((item, index) => ({
    ...item,
    rank: index + 1,
  }));
};

/**
 * Builds a preview list: top N rows plus the current user's row if outside top N.
 *
 * @param {Array<Object>} rankedEntries - already ranked
 * @param {string|null|undefined} currentUserId
 * @param {number} topN
 */
export const buildLeaderboardPreview = (rankedEntries, currentUserId, topN) => {
  const list = rankedEntries ?? [];
  const userIndex = list.findIndex((item) => item.players?.id === currentUserId);
  const preview = list.slice(0, topN);

  if (userIndex >= topN && userIndex !== -1) {
    const alreadyIncluded = preview.some((item) => item.players?.id === currentUserId);
    if (!alreadyIncluded) {
      preview.push(list[userIndex]);
    }
  }

  return {
    preview,
    userRank: userIndex !== -1 ? userIndex + 1 : null,
    userIndex,
  };
};

export const getScopeHelperText = (scope) =>
  scope === LEADERBOARD_SCOPES.ACTIVE
    ? 'Rank among players active in the last 2 months'
    : 'Rank among all players with at least one session this season';
